import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material';
import { Assessment, AssessmentService } from 'src/app/core/api/assessment.service';
import { PatientService } from 'src/app/core/api/patient.service';

interface EnrichedAssessment extends Assessment {
  parsedBody?: any;
  methodName?: string;
  categoryDisplay?: string;
}

@Component({
  selector: 'csi-select-assessments-modal',
  templateUrl: './select-assessments-modal.component.html',
  styleUrls: ['./select-assessments-modal.component.scss']
})
export class SelectAssessmentsModalComponent {
  allAssessments: EnrichedAssessment[] = [];
  readonly MAX = 4;
  selectedAssessments: EnrichedAssessment[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      currentCompared?: Assessment[];
      unaddedAssessments: Assessment[];
      maxSelectable: number;
    },
    public assessmentService: AssessmentService,
    public patientService: PatientService
  ) {
    const currentCompared = data && data.currentCompared ? data.currentCompared.slice() : [];
    const unaddedAssessments =
      data && data.unaddedAssessments ? data.unaddedAssessments.slice() : [];

    const assessmentsById = new Map<string, EnrichedAssessment>();

    const allRawAssessments = currentCompared.concat(unaddedAssessments);
    for (let i = 0; i < allRawAssessments.length; i++) {
      const currentAssessment = allRawAssessments[i];
      if (currentAssessment && currentAssessment.id) {
        const enriched = this.enrichAssessment(currentAssessment);
        assessmentsById.set(currentAssessment.id, enriched);
      }
    }

    this.allAssessments = Array.from(assessmentsById.values()).sort(function(a, b) {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

    this.selectedAssessments = [];
    for (let i = 0; i < currentCompared.length; i++) {
      const selected = this.enrichAssessment(currentCompared[i]);
      this.selectedAssessments.push(selected);
    }
  }

  private enrichAssessment(originalAssessment: Assessment): EnrichedAssessment {
    let parsedBody: any = null;
    let methodName = 'N/A';
    let categoryDisplay = '';

    try {
      parsedBody = originalAssessment.body ? JSON.parse(originalAssessment.body) : null;

      const methodKey =
        parsedBody && parsedBody.dryEyeForm && parsedBody.dryEyeForm.assessmentMethod;

      if (methodKey && this.assessmentService.encounterMethodFriendlyName) {
        const friendlyName = this.assessmentService.encounterMethodFriendlyName[methodKey];
        methodName = friendlyName || methodKey || 'N/A';
      }

      const category =
        parsedBody && parsedBody.assessmentForm && parsedBody.assessmentForm.category;

      if (category) {
        if (category.severity && category.type) {
          categoryDisplay = (category.severity + ' ' + category.type).replace(/\b\w/g, function(c) {
            return c.toUpperCase();
          });
        } else {
          categoryDisplay = category;
        }
      }
    } catch {}

    return {
      ...originalAssessment,
      parsedBody: parsedBody,
      methodName: methodName,
      categoryDisplay: categoryDisplay
    };
  }

  get atLimit(): boolean {
    return this.selectedAssessments.length >= this.MAX;
  }

  isSelected(assessment: Assessment): boolean {
    for (let i = 0; i < this.selectedAssessments.length; i++) {
      const selected = this.selectedAssessments[i];
      if (selected && selected.id === assessment.id) {
        return true;
      }
    }
    return false;
  }

  compareById(first: Assessment, second: Assessment): boolean {
    return first && second && first.id === second.id;
  }

  onSelectionChange(event: any) {
    if (!event || !event.option) return;

    if (event.option.selected && this.selectedAssessments.length > this.MAX) {
      event.option.selected = false;
      const index = this.selectedAssessments.indexOf(event.option.value);
      if (index > -1) {
        this.selectedAssessments.splice(index, 1);
      }
    }
  }
}
